import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AlertTriangle, CheckCircle2, Shield, Lock, Eye, Globe, ArrowRight } from "lucide-react";
import { useState, useEffect } from "react";

const Government = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Optimized Hero Section with Parallax Background */}
        <section className="relative py-20 md:py-28 lg:py-36 bg-cover bg-center bg-no-repeat min-h-[80vh] overflow-hidden">
          <picture className="absolute inset-0">
            <source 
              media="(max-width: 768px)" 
              srcSet="/lovable-uploads/ac914359-3b4c-40a2-acc3-57532dc5cc7c.png"
            />
            <div 
              className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat will-change-transform"
              style={{
                backgroundImage: `url('/lovable-uploads/ac914359-3b4c-40a2-acc3-57532dc5cc7c.png')`,
                transform: `translate3d(0, ${scrollY * 0.5}px, 0)`,
                backfaceVisibility: 'hidden',
                perspective: '1000px'
              }}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/70" />
          <div className="container mx-auto px-4 md:px-6 relative z-10 h-full flex items-center">
            <div className="max-w-4xl">
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-4 md:mb-6 text-white drop-shadow-2xl">
                <span className="text-white">Secure </span>
                <span className="text-[#8932c8]">Government</span>
                <span className="text-white"> Infrastructure</span>
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-white/90 mb-8 md:mb-12 drop-shadow-lg">
                Protect critical government systems, critical infrastructure, and citizen data with enterprise-grade security that meets the highest compliance standards and regulatory requirements including DPDP Act compliance.
              </p>
              
              {/* CTA Section */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="bg-[#8932c8] hover:bg-[#7a2cb3] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center gap-2 shadow-xl">
                  Request Demo
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button className="border-2 border-white text-white hover:bg-white hover:text-[#8932c8] px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-xl">
                  Know More
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Government-Specific Section */}
        <section className="py-10 md:py-16 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="bg-[#8932c8] text-white p-6 md:p-8 lg:p-12 rounded-xl mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Spharaka for Government</h2>
              <p className="text-base md:text-xl">Safeguarding democracy with next-generation cybersecurity</p>
            </div>

            <div className="mb-10 md:mb-16">
              <p className="text-base md:text-lg text-gray-700 mb-8 md:mb-12">
                Government agencies face unique cybersecurity challenges from nation-state actors, advanced persistent threats, and the need to protect sensitive citizen data and critical infrastructure. Spharaka's AI-powered XDR platform provides the advanced security posture required for government operations while ensuring compliance with federal security mandates and the Digital Personal Data Protection (DPDP) Act.
              </p>

              <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-center">Government Challenges & Spharaka Solutions</h3>

              <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-10 md:mb-16">
                {/* Challenge 1 */}
                <div className="border border-orange-200 bg-orange-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-orange-100 rounded-full text-orange-600">
                      <AlertTriangle size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Nation-State Threats</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Government agencies are prime targets for sophisticated nation-state actors with advanced capabilities and persistent attack campaigns.
                  </p>
                </div>

                {/* Solution 1 */}
                <div className="border border-violet-200 bg-blue-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-violet-100 rounded-full text-[#8932c8]">
                      <CheckCircle2 size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Advanced Threat Intelligence</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Leverages global threat intelligence and machine learning to identify and counter sophisticated attack patterns specific to government environments.
                  </p>
                </div>

                {/* Challenge 2 */}
                <div className="border border-orange-200 bg-orange-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-orange-100 rounded-full text-orange-600">
                      <AlertTriangle size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Compliance Requirements</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Meeting strict federal compliance standards like FISMA, NIST, and DPDP Act while maintaining operational efficiency.
                  </p>
                </div>

                {/* Solution 2 */}
                <div className="border border-violet-200 bg-blue-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-violet-100 rounded-full text-[#8932c8]">
                      <CheckCircle2 size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Built-in Compliance Framework</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Pre-configured compliance templates and automated reporting for FISMA, NIST, DPDP Act, and other federal security frameworks.
                  </p>
                </div>

                {/* Challenge 3 */}
                <div className="border border-orange-200 bg-orange-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-orange-100 rounded-full text-orange-600">
                      <AlertTriangle size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Legacy System Integration</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Government agencies often operate legacy systems that are difficult to secure and integrate with modern security tools.
                  </p>
                </div>

                {/* Solution 3 */}
                <div className="border border-violet-200 bg-blue-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-violet-100 rounded-full text-[#8932c8]">
                      <CheckCircle2 size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Flexible Deployment Options</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Supports air-gapped environments, on-premises deployment, and hybrid cloud configurations to work with existing infrastructure.
                  </p>
                </div>

                {/* Challenge 4 */}
                <div className="border border-orange-200 bg-orange-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-orange-100 rounded-full text-orange-600">
                      <AlertTriangle size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Budget Constraints</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Limited cybersecurity budgets while facing increasing threats and compliance requirements.
                  </p>
                </div>

                {/* Solution 4 */}
                <div className="border border-violet-200 bg-blue-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-violet-100 rounded-full text-[#8932c8]">
                      <CheckCircle2 size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Cost-Effective Intelligence</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Reduces operational costs through intelligent processes while maximizing security effectiveness, providing better ROI than traditional solutions.
                  </p>
                </div>
              </div>

              <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-center">Government-Specific Features</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                <div className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <Shield size={20} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">DPDP Act Compliance</p>
                    <p className="text-xs md:text-sm text-gray-600">Meet Digital Personal Data Protection Act requirements with our comprehensive compliance platform.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <Lock size={20} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Zero Trust Architecture</p>
                    <p className="text-xs md:text-sm text-gray-600">Implement executive order mandated zero trust principles across your infrastructure.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <Eye size={20} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Insider Threat Detection</p>
                    <p className="text-xs md:text-sm text-gray-600">Advanced behavioral analytics to detect potential insider threats and privilege abuse.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <Globe size={20} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Critical Infrastructure Protection</p>
                    <p className="text-xs md:text-sm text-gray-600">Specialized protections for SCADA, ICS, and other critical infrastructure systems.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={20} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Continuous Monitoring</p>
                    <p className="text-xs md:text-sm text-gray-600">24/7 security monitoring with government-grade security operations center support.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={20} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Incident Response</p>
                    <p className="text-xs md:text-sm text-gray-600">Rapid incident response capabilities aligned with government incident reporting requirements.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Government;
